/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Sinds je laatste bezoek" — the pure half of the comparer's changes strip
 * (plan §12.4 B3): a snapshot of what the visitor was shown, the diff of that
 * snapshot against the feed as it is now, the one localStorage slot that
 * carries the snapshot from visit to visit, and the tab's flag that the strip
 * was hidden (sessionStorage). No React, no i18n and no clock:
 * every "now" arrives as a parameter (readSnapshot falls back to the clock only
 * when it is given none), and the words belong to the strip.
 *
 * The strip says only what the snapshot proves (plan §12.2). The rules below
 * are that promise, written as code:
 *
 * - NUMBERS only: commission, yearly service costs, payout frequency and the
 *   ERE price. Never CMS text — a rewritten sentence is not a change this
 *   module can state.
 * - `changed` compares per row id (`providerId-contractId`, stable across
 *   renames) and lists one entry per changed field, in the row order of `rows`
 *   and, inside a row, as commission, service, payout. A field is skipped when
 *   either side is null/unknown: "unknown -> 15%" would read as a price move
 *   though nothing was ever stated before, and a payout value outside the
 *   vocabulary has no key, so it counts as unknown too. Payout is a
 *   provider-level setting, so one change shows once per row of that provider:
 *   the strip filters to the current view first and then merges equal lines.
 * - `price` is set only when the ERE price differs from the snapshot's.
 * - `added` / `gone` are provider NAMES, de-duplicated. A provider is new only
 *   when the snapshot held no row at all under its name, gone only when no row
 *   is left; one that gained or lost a single contract is neither. A rename
 *   reads as one new and one gone name: the name is the only identity the
 *   strip can state, and a `showInComparison` toggle looks the same as a
 *   withdrawal — hence "niet meer in de vergelijking", nothing stronger.
 * - `rows` is the WHOLE feed, never a filtered view (every offer outside the
 *   filters would read as gone) and never an empty one: callers skip the
 *   snapshot and the diff until `providers.length > 0`, as the shortlist does,
 *   because a snapshot of nothing would later read as "everything is new".
 *
 * Numbers are compared after rounding, strictly (no tolerance band). Row
 * figures go to cents: the feed prints at most cents (the flatten in
 * server/routes/providers.ts rounds a monthly fee to cents before it derives
 * the yearly total), so float noise such as 0.1 + 0.2 against 0.3 is not a
 * change. The ERE price goes to ten-thousandths, its own cap
 * (normalizeErePriceEntry in erePrice.js; formatErePrice prints a reading at
 * that precision): cents would hide 0,445 -> 0,449 exactly where the page
 * prints both. A value that is not a finite number is unknown, so neither NaN
 * nor the sign of zero ever decides a comparison.
 *
 * The snapshot stores the payout as its `table.payout_values` key
 * (`payoutValueKey`), not as the stored spelling: 'Per jaar' and the legacy
 * 'Jaarlijks' are one frequency, not a change. A stored key the vocabulary no
 * longer has (a snapshot from before a rename in payout.js) reads as unknown, so
 * a rename costs one visit's comparison instead of a false "uitbetaling" line on
 * every offer.
 *
 * An offer with a pricing model (src/lib/offerPricing.js — a staffel, a
 * vastrecht per kWh) is remembered by what does not move from week to week:
 * the percentage it shows at its audience's default volume (`c`) and the
 * model itself (`pm`, its JSON). The percentage is the NOMINAL one — a fixed
 * rate's is a function of the price itself, so it is remembered by its model
 * only — because the effective share of a vastrecht offer follows the weekly
 * ERE price, and "commissie 18,3% → 18,4%" after a price reading would report
 * a change nobody made. A changed model is one line of its own ("verdienmodel
 * gewijzigd"), and a flat offer is a model of none: `pm` absent reads as null
 * on BOTH sides, so a flat offer that turned into a model (and back) is
 * reported too — also for a snapshot from before models existed, which says
 * "flat" for every row, and rightly so.
 */

import type { EREProvider } from '../types';
import { siteDateParts } from './formatters';
import { defaultAnnualKwh, nominalCommissionPct, rowOfferPricing } from './offerPricing.js';
import { PAYOUT_VALUE_KEYS, payoutValueKey } from './payout';

/** The localStorage slot; the `v` inside the value is the format version. */
export const COMPARE_SNAPSHOT_KEY = 'ere:compare:snapshot';

/** sessionStorage: '1' once the visitor hid the strip in this tab (its "Verbergen"). */
export const CHANGES_DISMISSED_KEY = 'ere:compare:changes_dismissed';

/** One offer as it was shown: name, commission, service costs, payout key. */
export interface SnapshotRow {
  /** Provider name — kept so a row that is gone can still be named. */
  n: string;
  /** Commission in percent; null = unknown (0 is a genuine 0%). */
  c: number | null;
  /** Yearly service costs in euro; null = unknown (0 = explicitly free). */
  s: number | null;
  /** Payout frequency as its `table.payout_values` key; null = unknown. */
  p: string | null;
  /** The pricing model as JSON, on an offer that carries one (see the header); absent on a flat one. */
  pm?: string;
}

export interface CompareSnapshot {
  v: 1;
  /** When the visitor was shown it (epoch ms) — the caller's clock, not ours. */
  at: number;
  erePrice: number;
  /** Keyed by row id. */
  rows: Record<string, SnapshotRow>;
}

export type ChangeField = 'commission' | 'service' | 'payout' | 'model';

/**
 * One changed field of one row, named as it is called now. `then` / `now` are
 * cents-rounded numbers for commission (percent) and service (euro a year); for
 * payout they are `table.payout_values` keys, never a stored spelling; for the
 * model the two models' JSON (the strip names the change, not the JSON), with
 * null for a flat offer (no model). A figure with an unknown side is skipped,
 * so only a model change has a null side.
 */
export interface RowChange {
  id: string;
  name: string;
  field: ChangeField;
  then: number | string | null;
  now: number | string | null;
}

export interface CompareDiff {
  /** The ERE price at its own precision; null when it did not move. */
  price: { then: number; now: number } | null;
  changed: RowChange[];
  /** Provider names with a row now and none in the snapshot. */
  added: string[];
  /** Provider names with a row in the snapshot and none now. */
  gone: string[];
}

const toCents = (n: number): number => Math.round(n * 100) / 100;
const toTenThousandths = (n: number): number => Math.round(n * 10000) / 10000;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** A row figure at cents precision, or null when it is not a number (unknown). */
const amount = (value: unknown): number | null => (isFiniteNumber(value) ? toCents(value) : null);

/** `payoutValueKey` folds the legacy spellings and 'Onbekend' into one key or null. */
const payoutKey = (value: EREProvider['payoutFrequency']): string | null =>
  payoutValueKey(value) ?? null;

const PAYOUT_KEYS: ReadonlySet<string> = new Set(Object.values(PAYOUT_VALUE_KEYS));

/**
 * The commission a row is remembered and compared by (see the header): a flat
 * row's `commissionFee`; a row with a pricing model the percentage it shows at
 * its audience's default volume; a fixed rate none.
 */
function commissionOf(row: EREProvider): number | null {
  if (!row.pricing) return amount(row.commissionFee);
  const pricing = rowOfferPricing(row);
  if (pricing?.model === 'fixed_rate') return null;
  return amount(nominalCommissionPct(pricing, defaultAnnualKwh(row.targetAudience)));
}

/** The row's pricing model as JSON (its sanitised key order), or null for a flat row. */
const modelOf = (row: EREProvider): string | null =>
  row.pricing ? JSON.stringify(rowOfferPricing(row)) : null;

/** A stored payout key if the vocabulary still has it, else null (unknown). */
const storedPayoutKey = (key: string | null): string | null =>
  key !== null && PAYOUT_KEYS.has(key) ? key : null;

/**
 * What the visitor is being shown right now. `rows` is the whole feed (see the
 * header); the row figures are stored at the precision they are compared at.
 */
export function snapshotOf(
  rows: readonly EREProvider[],
  erePrice: number,
  at: number
): CompareSnapshot {
  const byId: Record<string, SnapshotRow> = {};
  for (const row of rows) {
    if (!row || !row.id) continue;
    const pm = modelOf(row);
    byId[row.id] = {
      n: typeof row.name === 'string' ? row.name : '',
      c: commissionOf(row),
      s: amount(row.serviceCosts),
      p: payoutKey(row.payoutFrequency),
      ...(pm !== null ? { pm } : {}),
    };
  }
  return { v: 1, at, erePrice, rows: byId };
}

// The compared fields, in the order a row's changes are listed.
const FIELDS: ReadonlyArray<{
  field: ChangeField;
  then: (old: SnapshotRow) => number | string | null;
  now: (row: EREProvider) => number | string | null;
}> = [
  { field: 'commission', then: (old) => amount(old.c), now: commissionOf },
  { field: 'service', then: (old) => amount(old.s), now: (row) => amount(row.serviceCosts) },
  {
    field: 'payout',
    then: (old) => storedPayoutKey(old.p),
    now: (row) => payoutKey(row.payoutFrequency),
  },
  // `pm` absent is "no model" on both sides: a flat offer turning into a model
  // (and back) is a change, which the loop below lets through for this field.
  { field: 'model', then: (old) => old.pm ?? null, now: modelOf },
];

function priceChange(then: number, now: number): CompareDiff['price'] {
  if (!isFiniteNumber(then) || !isFiniteNumber(now)) return null;
  const was = toTenThousandths(then);
  const is = toTenThousandths(now);
  return was === is ? null : { then: was, now: is };
}

/**
 * What differs between the snapshot and the feed as it is now (rules in the
 * header). Pure: neither argument is touched.
 */
export function diffSnapshots(
  prev: CompareSnapshot,
  rows: readonly EREProvider[],
  erePrice: number
): CompareDiff {
  // Own entries only, in insertion order. Row ids hold a dash, so they are
  // never integer-like keys and `gone` keeps the order the snapshot was made in.
  const before = Object.entries(prev.rows);
  const rowBefore = new Map(before);
  const namesBefore = new Set<string>();
  for (const [, old] of before) if (old.n) namesBefore.add(old.n);

  const changed: RowChange[] = [];
  const namesNow = new Set<string>();
  const added = new Set<string>();
  for (const row of rows) {
    if (!row || !row.id) continue;
    if (row.name) {
      namesNow.add(row.name);
      if (!namesBefore.has(row.name)) added.add(row.name);
    }
    const old = rowBefore.get(row.id);
    if (!old) continue;
    for (const { field, then, now } of FIELDS) {
      const was = then(old);
      const is = now(row);
      // An unknown side is skipped for a figure; for the model, null is a
      // flat offer and a real side of the comparison.
      const comparable = field === 'model' || (was !== null && is !== null);
      if (comparable && was !== is) {
        changed.push({ id: row.id, name: row.name, field, then: was, now: is });
      }
    }
  }

  const gone = new Set<string>();
  for (const [, old] of before) if (old.n && !namesNow.has(old.n)) gone.add(old.n);

  return {
    price: priceChange(prev.erePrice, erePrice),
    changed,
    added: [...added],
    gone: [...gone],
  };
}

export function isEmptyDiff(diff: CompareDiff): boolean {
  return (
    diff.price === null &&
    diff.changed.length === 0 &&
    diff.added.length === 0 &&
    diff.gone.length === 0
  );
}

/**
 * Same calendar day in the site's zone (Europe/Amsterdam) — the zone the strip
 * prints the visit date in (`formatDateShort`), so the guard and the heading
 * cannot disagree, and the answer does not depend on the machine's own zone.
 * Resolved through `siteDateParts`, the client's instant-to-Amsterdam-day
 * helper (server/data/dayKeys.ts is the server's copy of the same idea). A
 * moment that is not a time is never "the same day".
 */
export function isSameLocalDay(a: number, b: number): boolean {
  const first = siteDateParts(new Date(a));
  const second = siteDateParts(new Date(b));
  return (
    first !== null &&
    second !== null &&
    first.year === second.year &&
    first.month === second.month &&
    first.day === second.day
  );
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isAmountOrNull = (value: unknown): value is number | null =>
  value === null || isFiniteNumber(value);

const isStringOrNull = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

/**
 * The stored value as a snapshot, or null unless it has exactly the shape we
 * write. One malformed row rejects the whole snapshot: a row dropped from it
 * would read as new on the next diff, and that is a claim it cannot prove.
 * Fields we do not know are not carried over.
 */
function parseSnapshot(value: unknown): CompareSnapshot | null {
  if (!isRecord(value) || value.v !== 1) return null;
  const { at, erePrice, rows } = value;
  if (!isFiniteNumber(at) || !isFiniteNumber(erePrice) || !isRecord(rows)) return null;
  const byId: Record<string, SnapshotRow> = {};
  for (const [id, row] of Object.entries(rows)) {
    if (!isRecord(row)) return null;
    const { n, c, s, p, pm } = row;
    if (typeof n !== 'string' || !isAmountOrNull(c) || !isAmountOrNull(s) || !isStringOrNull(p)) {
      return null;
    }
    if (pm !== undefined && typeof pm !== 'string') return null;
    byId[id] = { n, c, s, p, ...(typeof pm === 'string' ? { pm } : {}) };
  }
  return { v: 1, at, erePrice, rows: byId };
}

/**
 * A stamp further ahead than this is a clock that was wrong when it wrote, not
 * a visit (the rule of src/lib/compareMemory.ts).
 */
const FUTURE_TOLERANCE_MS = 24 * 60 * 60 * 1000;

/**
 * The remembered snapshot; null when absent, unreadable, not ours, or stamped
 * more than a day after `now` — "since your last visit" cannot be dated after
 * the visit it is shown on. Never throws.
 */
export function readSnapshot(now: number = Date.now()): CompareSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COMPARE_SNAPSHOT_KEY);
    const snapshot = raw ? parseSnapshot(JSON.parse(raw)) : null;
    if (snapshot && snapshot.at > now + FUTURE_TOLERANCE_MS) return null;
    return snapshot;
  } catch {
    return null;
  }
}

/** Remember the snapshot. Never throws: with storage refused the strip just has nothing to compare next time. */
export function writeSnapshot(snapshot: CompareSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COMPARE_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // Private mode, quota or blocked site data.
  }
}

/**
 * True once the visitor hid the strip in this tab (the resume chip's idiom,
 * src/lib/compareMemory.ts). False when sessionStorage is unavailable.
 */
export function isChangesDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(CHANGES_DISMISSED_KEY) === '1';
  } catch {
    return false; // storage blocked: behave as never hidden
  }
}

/** Hide the strip for the rest of this tab. A no-op when sessionStorage is unavailable. */
export function dismissChangesForSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(CHANGES_DISMISSED_KEY, '1');
  } catch {
    // Storage blocked: the strip's own state still hides it for this page view.
  }
}
